/**
 * @fileoverview Shared user profile dropdown component for system-web and tenant-web.
 * @module ui-admin/components/profile/UserProfileDropdown
 *
 * Responsibilities:
 * - Render the authenticated user's identity (name, email).
 * - Render an inline theme swatch grid so the user can pick a personal theme override.
 * - Show the effective theme source (user / department / app default).
 * - Provide a link to clear the user's personal theme override.
 * - Provide navigation to Settings / Preferences.
 * - Provide a Sign Out action.
 *
 * Dependencies:
 * - ThemeSwatchGrid (shared swatch grid)
 * - GridTheme, THEMES, useTheme (from ui-admin ThemeContext)
 * - lucide-react icons
 *
 * Architecture:
 * - Pure presentation component. Does NOT call Dexie, API, or context directly.
 * - All data and actions are injected through props by the consuming layout component
 *   (TopBar in tenant-web, AdminLayout in system-web).
 * - Theme selection persistence is handled by the parent through onSelectTheme.
 *
 * Notes:
 * - The component uses inline styles derived from the injected `theme` prop so it
 *   remains theme-aware without calling useTheme() itself.
 * - Optional actions (onClearOverride, onPreferences, onHelp, onSignOut) are omitted
 *   from the UI when not supplied.
 */

import React from 'react';
import { Settings, HelpCircle, LogOut, X, Palette } from 'lucide-react';
import type { GridTheme } from '../../contexts/ThemeContext';
import { ThemeSwatchGrid } from './ThemeSwatchGrid';

/**
 * @contract UserProfileDropdownProps
 *
 * Purpose:
 * - Props for the UserProfileDropdown component.
 *
 * Fields:
 * - user: Authenticated user's display name and email.
 * - theme: Currently active GridTheme (used for inline styling).
 * - themes: Full list of available themes.
 * - effectiveThemeId: Currently active theme id (highlighted in the swatch grid).
 * - userThemeId: User's personal override or null.
 * - tenantDefaultThemeId: Department default theme id or null.
 * - themeSource: Label for which precedence layer determined the current theme.
 * - onSelectTheme: Called when user picks a new theme swatch.
 * - onClearOverride: Called when user clicks "Use department default". Omit to hide.
 * - onPreferences: Called when Preferences item is clicked. Omit to hide.
 * - onHelp: Called when Help item is clicked. Omit to hide.
 * - onSignOut: Called when Sign Out is clicked. Omit to hide.
 *
 * Rules:
 * - All action callbacks are optional; missing ones hide their corresponding UI element.
 *
 * Sync:
 * - Pure presentation; persistence is the caller's responsibility.
 */
export interface UserProfileDropdownProps {
  user: { name: string; email: string } | null;
  theme: GridTheme;
  themes: GridTheme[];
  effectiveThemeId: string;
  userThemeId: string | null;
  tenantDefaultThemeId?: string | null;
  themeSource?: 'user' | 'tenant' | 'app-default';
  onSelectTheme: (themeId: string) => void;
  onClearOverride?: () => void;
  onPreferences?: () => void;
  onHelp?: () => void;
  onSignOut?: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
  user: 'Your choice',
  tenant: 'Department default',
  'app-default': 'App default',
};

/**
 * @component UserProfileDropdown
 *
 * Purpose:
 * - Render the profile dropdown panel used in both portal headers.
 *
 * Props:
 * - See UserProfileDropdownProps above.
 *
 * Behavior:
 * - Identity section at top (name, email).
 * - Theme section with swatch grid and source label.
 * - Optional "Use department default" link when user has a personal override.
 * - Action items (Preferences, Help, Sign Out) at bottom.
 *
 * Data Source:
 * - local (props only)
 *
 * Constraints:
 * - No Dexie, API, or context calls. All data comes from props.
 *
 * Performance:
 * - Renders synchronously from props; no internal state.
 *
 * @reusable
 *
 * Usage:
 * - tenant-web/layouts/TopBar.tsx
 * - system-web/layouts/AdminLayout.tsx
 *
 * Rules:
 * - MUST be presentation-only; stateful persistence logic belongs in the consuming layout.
 */
export function UserProfileDropdown({
  user,
  theme,
  themes,
  effectiveThemeId,
  userThemeId,
  tenantDefaultThemeId,
  themeSource = 'app-default',
  onSelectTheme,
  onClearOverride,
  onPreferences,
  onHelp,
  onSignOut,
}: UserProfileDropdownProps) {
  const sourceLabel = SOURCE_LABELS[themeSource] ?? 'App default';

  return (
    <div
      className="absolute right-0 top-full mt-1 rounded-lg shadow-lg py-1 z-50"
      style={{
        background: theme.cardBg,
        border: `1px solid ${theme.cardBorder}`,
        minWidth: 220,
      }}
    >
      {/* Identity */}
      <div
        className="px-3 py-2"
        style={{ borderBottom: `1px solid ${theme.cardBorder}` }}
      >
        <p className="text-sm font-medium truncate" style={{ color: theme.textPrimary }}>
          {user?.name ?? 'Unknown'}
        </p>
        <p className="text-xs truncate" style={{ color: theme.textMuted }}>
          {user?.email ?? ''}
        </p>
      </div>

      {/* Theme picker */}
      <div
        className="px-3 pt-2 pb-1"
        style={{ borderBottom: `1px solid ${theme.cardBorder}` }}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="flex items-center gap-1 text-xs font-medium" style={{ color: theme.textSecondary }}>
            <Palette size={11} />
            Theme
          </span>
          <span className="text-xs" style={{ color: theme.textMuted }}>
            {sourceLabel}
          </span>
        </div>

        <ThemeSwatchGrid
          themes={themes}
          selectedThemeId={effectiveThemeId}
          tenantDefaultThemeId={tenantDefaultThemeId}
          onSelectTheme={onSelectTheme}
        />

        {onClearOverride && userThemeId && (
          <button
            onClick={onClearOverride}
            className="flex items-center gap-1 text-xs mt-1 px-1"
            style={{ color: theme.textMuted }}
            onMouseEnter={(e) => (e.currentTarget.style.color = theme.textPrimary)}
            onMouseLeave={(e) => (e.currentTarget.style.color = theme.textMuted)}
          >
            <X size={10} />
            Use department default
          </button>
        )}
      </div>

      {/* Action items */}
      {onPreferences && (
        <button
          onClick={onPreferences}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm"
          style={{ color: theme.textPrimary }}
          onMouseEnter={(e) => (e.currentTarget.style.background = theme.rowHoverBg)}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <Settings size={14} />
          Preferences
        </button>
      )}
      {onHelp && (
        <button
          onClick={onHelp}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm"
          style={{ color: theme.textPrimary }}
          onMouseEnter={(e) => (e.currentTarget.style.background = theme.rowHoverBg)}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <HelpCircle size={14} />
          Help
        </button>
      )}
      {onSignOut && (
        <>
          <div style={{ borderTop: `1px solid ${theme.cardBorder}` }} />
          <button
            onClick={onSignOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600"
            onMouseEnter={(e) => (e.currentTarget.style.background = '#fef2f2')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </>
      )}
    </div>
  );
}
